/* eslint-disable max-lines-per-function */
import React from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";

import { Alert, Button, ButtonProps, CircularProgress, Grid } from "@mui/material";
import logger from "services/logger";

import { useSnackbar } from "components/haven-design/snackbar";
import {
  AdminArchiveAccountMutation,
  AdminArchiveAccountMutationVariables,
  AdminDetailedListUsersQuery,
  AdminDetailedListUsersQueryVariables,
  AdminUpdateAccountMutation,
  AdminUpdateAccountMutationVariables,
  AdminUserProfileInput,
  PostgresUserRole,
} from "types/gql-types";
import Form, { FormProps } from "./form";
import InterestsList from "./components/interest-list";

import { ARCHIVE_ACCOUNT_MUTATIION, DETAILED_LIST_USERS_QUERY, RESTORE_ACCOUNT_MUTATIION, UpdateAccountMutation } from "./queries";
import { ErrorCodes } from "./new";

const confirmPrompt = `
Are you sure you want to archive this user?

This will free their login email and phone
number to be used by other accounts but will
lock them out from being able to login.

Please remember to transfer all of the users
interests to a new user when applicable.
`;

const ArchivedWarning: React.FC = () => (
  <Alert severity="warning" style={{ marginBottom: "16px", maxWidth: "800px" }}>
    <strong>This user is archived</strong>
    <br />
    Archived users cannot log in to the system. You can restore them from the team
    settings page.
  </Alert>
);

const ApplicantDetails: React.FC = () => {
  const { openSnackbar } = useSnackbar();
  const { id } = useParams<{ id: string }>();

  const { data, loading, error } = useQuery<
  AdminDetailedListUsersQuery,
  AdminDetailedListUsersQueryVariables
  >(DETAILED_LIST_USERS_QUERY, { variables: { id } });

  const [updateUser] = useMutation<
  AdminUpdateAccountMutation,
  AdminUpdateAccountMutationVariables
  >(UpdateAccountMutation, {
    refetchQueries: ["AdminListUsersQuery"],
  });

  const [archiveUser] = useMutation<
  AdminArchiveAccountMutation,
  AdminArchiveAccountMutationVariables
  >(ARCHIVE_ACCOUNT_MUTATIION, {
    refetchQueries: ["AdminListUsersQuery", "AdminDetailedListUsersQuery"],
  });

  const [restoreUser] = useMutation<
  AdminArchiveAccountMutation,
  AdminArchiveAccountMutationVariables
  >(RESTORE_ACCOUNT_MUTATIION, {
    refetchQueries: ["AdminListUsersQuery", "AdminDetailedListUsersQuery"],
  });

  const [user] = data?.adminApplicants || [];

  if (loading) {
    return (
      <Grid container justifyContent="center">
        <CircularProgress />
      </Grid>
    );
  }

  if (error || !user) return <p>Error loading applicant account.</p>;

  const handleUpdateUser: FormProps["onSubmit"] = async (variables, formikHelpers) => {
    try {
      const roles = variables.roles as PostgresUserRole[];
      const profile = variables.profile as AdminUserProfileInput;
      await updateUser({ variables: { id: user.id,
        ...variables,
        roles,
        profile: {
          ...profile,
          firstName: profile.firstName && profile.firstName.trim(),
          lastName: profile.lastName && profile.lastName.trim(),
        } } });

      openSnackbar({ message: "Updated Account Successfully", status: "success" });
    } catch (e) {
      logger.sendError(e as Error);
      openSnackbar({ message: "Updating Account Failed", status: "error" });
      switch ((e as Error).message) {
        case ErrorCodes.DuplicateEmailAccount:
          formikHelpers.setFieldError("user.loginEmail", "Already exists");
          break;
        case ErrorCodes.DuplicatePhoneAccount:
          formikHelpers.setFieldError("user.loginPhone", "Already exists");
          break;
        case ErrorCodes.InvalidPassword:
          formikHelpers.setFieldError("user.password", "Incorrect password");
          break;

        default:
          console.error(e);
          break;
      }
    }
  };

  const handleRestoreUser: ButtonProps["onClick"] = async (event) => {
    event.preventDefault();

    try {
      await restoreUser({ variables: { id: id ?? "" } });
      openSnackbar({ message: "Restored Account Successfully", status: "success" });
    } catch (e) {
      console.error(e);

      // Handle specific error cases
      const errorMessage = (e as Error).message;
      switch (errorMessage) {
        case "login_email_taken":
          openSnackbar({
            message: "Cannot restore: Email is already in use by another active account",
            status: "error",
          });
          break;
        case "login_phone_taken":
          openSnackbar({
            message: "Cannot restore: Phone number is already in use by another active account",
            status: "error",
          });
          break;
        case "missing_login_method":
          openSnackbar({
            message: "Cannot restore: User must have either email with password or phone number",
            status: "error",
          });
          break;
        default:
          openSnackbar({ message: "Un-Archiving Account Failed", status: "error" });
          break;
      }
    }
  };

  const handleArchiveUser: ButtonProps["onClick"] = async (event) => {
    event.preventDefault();

    // eslint-disable-next-line no-alert
    if (!confirm(confirmPrompt)) return;

    try {
      await archiveUser({ variables: { id: id ?? "" } });

      openSnackbar({ message: "Archived Account Successfully", status: "success" });
    } catch (e) {
      console.error(e);
      openSnackbar({ message: "Archiving Account Failed", status: "error" });
    }
  };

  const toggleArchiveUser: ButtonProps["onClick"] = async (event) => {
    if (user.isArchived) {
      await handleRestoreUser(event);
    } else {
      await handleArchiveUser(event);
    }
  };

  return (
    <div style={{ padding: "1rem" }}>
      <Grid container spacing={2}>
        {user.isArchived && <Grid size={{ xs: 12 }} container>
          <ArchivedWarning />
        </Grid>}
        <Grid size={{ xs: 12 }} container justifyContent="flex-end">
          <Button
            variant="outlined"
            color="secondary"
            onClick={toggleArchiveUser}
          >
            {user.isArchived ? "Restore" : "Archive"}
          </Button>
        </Grid>

        <Form user={user} onSubmit={handleUpdateUser} />

        <Grid size={{ xs: 12 }}>
          <dl>
            <dt>ID:</dt>
            <dd>{user.id}</dd>

            {user.organization && (
              <>
                <dt>Organization:</dt>
                <dd>
                  <Link to={`/dash/admin/organizations/${user.organization.id}`}>
                    {user.organization.name}
                  </Link>
                </dd>
              </>
            )}

            <dt># of Interests:</dt>
            <dd>{user.interests?.length || 0}</dd>
          </dl>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <hr />

          <h3>Interests</h3>
          <InterestsList interests={user.interests} />
        </Grid>
      </Grid>
    </div>
  );
};

export default ApplicantDetails;
